import { Component, inject, OnInit, Output, Input } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  IKeheModalEvent,
  KeheModalEventsTypes,
  PhoenixModalModule,
} from '@kehe/phoenix-modal';
import { CommonModule } from '@angular/common';
import { PhoenixAlertsModule } from '@kehe/phoenix-notifications';
import { PhoenixButtonModule } from '@kehe/phoenix-button';
import { PhoenixErrorsModule } from '@kehe/phoenix-errors';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import {
  FormFieldModule,
  RadioButtonModule,
  TextBoxModule,
} from '@progress/kendo-angular-inputs';
import { Destroyable } from '@app/abstract/destroyable';
import { AddTransactionTypeStore } from './add-transaction-type-modal.store';
import { ComboBoxModule } from '@progress/kendo-angular-dropdowns';
import { IFileUpload, PhoenixUploadModule } from '@kehe/phoenix-upload';
import { cleanPastedInput } from '@app/utils/text-paste-utils';
import { isKeyANumber } from '@app/utils/text-validation-utils';
import { TransactionTypeListStore } from '../transaction-type-grid/transaction-type-grid.store';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AddTransactionTypeForm } from './add-transaction-type.form';
import { CodeValue } from '@app/models/code-value';

@Component({
  selector: 'app-add-transaction-type-modal',
  templateUrl: './add-transaction-type-modal.component.html',
  styleUrls: ['./add-transaction-type-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PhoenixAlertsModule,
    PhoenixModalModule,
    ComboBoxModule,
    PhoenixButtonModule,
    PhoenixUploadModule,
    PhoenixErrorsModule,
    DateInputsModule,
    FormFieldModule,
    FormsModule,
    LabelModule,
    TextBoxModule,
    RadioButtonModule,
    ReactiveFormsModule,
  ],
  providers: [AddTransactionTypeStore, TransactionTypeListStore],
})
export class AddTransactionTypeModalComponent
  extends Destroyable
  implements OnInit
{
  @Input() editMode: boolean = false;
  @Input() editData: any | null = null;
  originalEditSnapshot: any | null = null;

  addTransactionTypeForm = inject(AddTransactionTypeForm);
  private _componentStore = inject(AddTransactionTypeStore);
  private _transactionListStore = inject(TransactionTypeListStore);
  uploadRestrictions = {
    allowedExtensions: ['.xlsx'],
    // 5MB upload limit
    maxFileSize: 5 * 1024 * 1024,
  };
  supportingDocumentUploadRestrictions = {
    allowedExtensions: ['.pdf'],
    // 5MB upload limit
    maxFileSize: 5 * 1024 * 1024,
  };
  fileUploads: IFileUpload[] = [];
  supportingDocumentFileUploads: IFileUpload[] = [];
  isSelectedFileValid: boolean = false;
  isSelectedSupportingDocumentFileValid: boolean = false;
  formGroup = this._componentStore.formGroup;
  invoicePrefixCharacterCount = 0;
  invoicePrefixMaxLength = 5;
  costCenterCharacterCount = 0;
  transactionTypes = [];
  hasDuplicateError = false;
  isHandKeyFFEnabled = false;

  vm$ = this._componentStore.vm$;
  destroy$: Subject<void> = new Subject<void>();
  @Output() closeModal = this._componentStore.closeModalEmitter;

  costCenters: CodeValue[] = [];
  loadingCostCenters = false;

  ngOnInit(): void {
    this.addTransactionTypeForm.initializeForm();
    this._componentStore.getSpendCategory$({ filterValue: '' });
    this._componentStore.loadCostCenters('');

    if (this.editMode && this.editData) {
      this.enterEditMode(this.editData);
    } else {
      this.addTransactionTypeForm.form
        .get('invoicePrefix')
        ?.enable({ emitEvent: false });
    }

    this._transactionListStore.vm$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vm) => {
        this.transactionTypes = vm.allData || [];
      });

    this.vm$.pipe(takeUntil(this.destroy$)).subscribe((vm) => {
      this.costCenters = vm.costCenters ?? [];
      this.loadingCostCenters = vm.costCentersLoading;
      this.isHandKeyFFEnabled = vm.isHandKeyFFEnabled;

      if (this.isHandKeyFFEnabled === false) {
        this.addTransactionTypeForm.removeIsHandKeyControl();
      }

      if (vm.hasError) {
        this.scrollModalToTop();
      }
    });

    this.addTransactionTypeForm.form.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasDuplicateError = this.editMode
          ? false
          : this.checkForDuplicateInvoicePrefix();
      });
  }

  private scrollModalToTop(): void {
    const modalContainer = document.getElementById(
      'add-transaction-type-modal',
    );
    modalContainer
      ?.querySelector('.body')
      ?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  checkForDuplicateInvoicePrefix(): boolean {
    const invoicePrefix =
      this.addTransactionTypeForm.form.get('invoicePrefix')?.value;
    const invoicePrefixControl =
      this.addTransactionTypeForm.form.get('invoicePrefix');

    if (!invoicePrefix || !invoicePrefix.trim()) {
      if (invoicePrefixControl?.hasError('duplicate')) {
        const errors = { ...invoicePrefixControl.errors };
        delete errors['duplicate'];
        invoicePrefixControl.setErrors(
          Object.keys(errors).length > 0 ? errors : null,
        );
      }
      return false;
    }

    const isDuplicate = this.transactionTypes.some(
      (item) =>
        item.code?.trim().toLowerCase() === invoicePrefix.trim().toLowerCase(),
    );

    if (isDuplicate) {
      invoicePrefixControl?.setErrors({
        ...invoicePrefixControl.errors,
        duplicate: true,
      });
    } else {
      if (invoicePrefixControl?.hasError('duplicate')) {
        const errors = { ...invoicePrefixControl.errors };
        delete errors['duplicate'];
        invoicePrefixControl.setErrors(
          Object.keys(errors).length > 0 ? errors : null,
        );
      }
    }

    return isDuplicate;
  }

  handleModalEvents(event: IKeheModalEvent) {
    if (event.type === KeheModalEventsTypes.Close) {
      this.handleCloseModal();
      this.originalEditSnapshot = null;
      this.editMode = false;
      this.editData = null;
    }
  }

  handleCloseModal() {
    this.closeModal.emit();
  }

  setRequiredDigitsError(control: AbstractControl) {
    if (
      control &&
      control.value !== null &&
      control.value !== '' &&
      control.value.toString().length < 3
    ) {
      control.setErrors({ isRequiredDigits: true });
    }
  }

  costCenterValueChange(value: unknown): void {
    if (value) {
      this.addTransactionTypeForm.form.get('costCenter')?.setValue(value);
    } else {
      this.addTransactionTypeForm.form.get('costCenter')?.setValue(null);
    }
  }

  spendCategoryValueChange(value: any): void {
    if (value) {
      this.addTransactionTypeForm.form.get('spendCategory')?.setValue(value);
    } else {
      this.addTransactionTypeForm.form.get('spendCategory')?.setValue(null);
    }
  }

  handleAddFiles(files: IFileUpload[]): void {
    this.fileUploads = files;
    this.updateFilesForUpload(this.fileUploads);
    this.runValidationsForUploadedFile(this.fileUploads);
    this.markFileUploadChanged();
  }

  handleSupportingDocumentFiles(files: IFileUpload[]): void {
    this.supportingDocumentFileUploads = files;
    this.updateSupportingDocumentFilesForUpload(
      this.supportingDocumentFileUploads,
    );
    this.runValidationsForUploadedSupportingDocumentFile(
      this.supportingDocumentFileUploads,
    );
    this.markSupportingDocumentFileChanged();
  }

  handleRemoveFiles(file: IFileUpload): void {
    this.fileUploads = [...this.fileUploads].filter((f) => f.uid !== file.uid);
    this.isSelectedFileValid = false;
    this.addTransactionTypeForm.form
      .get('fileUpload')
      ?.setValue(this.fileUploads);
  }

  handleRemoveSupportingDocumentFiles(file: IFileUpload): void {
    this.supportingDocumentFileUploads = [
      ...this.supportingDocumentFileUploads,
    ].filter((f) => f.uid !== file.uid);
    this.isSelectedSupportingDocumentFileValid = false;
    this.addTransactionTypeForm.form
      .get('supportingDocumentFile')
      ?.setErrors({ required: true });
  }

  updateFilesForUpload(files: IFileUpload[]) {
    files.forEach((file) => {
      this.fileUploads = [
        {
          ...file,
          progress: 100,
          uploading: false,
          uploadComplete: true,
        },
      ];
      this.addTransactionTypeForm.form
        .get('fileUpload')
        ?.setValue(this.fileUploads);
    });
  }

  updateSupportingDocumentFilesForUpload(files: IFileUpload[]) {
    files.forEach((file) => {
      this.supportingDocumentFileUploads = [
        {
          ...file,
          progress: 100,
          uploading: false,
          uploadComplete: true,
        },
      ];
      this.addTransactionTypeForm.form
        .get('supportingDocumentFile')
        ?.setValue(this.supportingDocumentFileUploads);
    });
  }

  runValidationsForUploadedFile(files: IFileUpload[]) {
    const fileUploadControl =
      this.addTransactionTypeForm.form.get('fileUpload');
    if (files && files.length > 0 && files[0].name.endsWith('.xlsx')) {
      this.isSelectedFileValid = this.isUploadedFileValid(files);
      fileUploadControl?.setErrors(null);
    } else {
      this.isSelectedFileValid = false;
      fileUploadControl?.setErrors({ invalidFileType: true });
    }
  }

  runValidationsForUploadedSupportingDocumentFile(files: IFileUpload[]) {
    const fileUploadControl = this.addTransactionTypeForm.form.get(
      'supportingDocumentFile',
    );
    const hasSupportingDocument = this.addTransactionTypeForm.form.get(
      'hasSupportingDocument',
    )?.value;
    if (!hasSupportingDocument) {
      fileUploadControl?.setErrors(null);
      return;
    }
    if (files && files.length > 0 && files[0].name.endsWith('.pdf')) {
      this.isSelectedSupportingDocumentFileValid =
        this.isUploadedFileValid(files);
      fileUploadControl?.setErrors(null);
    } else {
      this.isSelectedSupportingDocumentFileValid = false;
      fileUploadControl?.setErrors({ invalidFileType: true });
    }
  }

  isUploadedFileValid(files: IFileUpload[]) {
    return files.some(
      (file) => !file.validationErrors || file.validationErrors?.length === 0,
    );
  }

  onBlurEvent() {
    // TODO: Implement on blur event functionality
  }

  handleInvoicePrefixValueChange(invoicePrefix: string): void {
    this.invoicePrefixCharacterCount = invoicePrefix.length;
  }

  handleInvoicePrefixBlur(): void {
    this.addTransactionTypeForm.form
      .get('invoicePrefix')
      .updateValueAndValidity();
  }

  handlePasteNumericValues(pasteEvent: ClipboardEvent): boolean {
    const pastedText = pasteEvent.clipboardData.getData('text/plain');
    const cleanedInput = cleanPastedInput(pastedText);
    this.addTransactionTypeForm.form.get('costCenter').setValue(+cleanedInput);
    return false;
  }

  handleCostCenterValueChange(costCenter: string): void {
    this.costCenterCharacterCount = costCenter.length;
  }

  handleCostCenterBlur(): void {
    this.addTransactionTypeForm.form.get('costCenter').updateValueAndValidity();
  }

  handleKeyDown(event: KeyboardEvent) {
    return isKeyANumber(event) && event.key !== ' ';
  }

  getFormErrors(): string[] {
    const errors: string[] = [];

    if (!this.addTransactionTypeForm.form) {
      return errors;
    }

    Object.keys(this.addTransactionTypeForm.form.controls).some((key) => {
      const control = this.addTransactionTypeForm.form.get(key);
      if (
        control &&
        control.hasError('required') &&
        (control.touched || control.dirty)
      ) {
        errors.push('required');
      }
    });

    if (this.hasDuplicateError) {
      errors.push('Invoice Prefix already exists.');
    }

    this.prefixAlphaNumbericValidation(errors);

    return errors;
  }

  prefixAlphaNumbericValidation(errors) {
    const invoicePrefixCtrl =
      this.addTransactionTypeForm.form.get('invoicePrefix');

    if (
      invoicePrefixCtrl?.hasError('nonAlphaNumeric') &&
      (invoicePrefixCtrl.dirty || invoicePrefixCtrl.touched)
    ) {
      errors.push('Invoice Prefix must be alphanumeric.');
    }
  }

  get formErrorsHtml(): string {
    const errors = this.getFormErrors();

    if (errors.length === 0) {
      return '';
    }

    return `
    <div>
      <p class="mb-1">You have errors below that require your attention.</p>
    </div>
  `;
  }

  save() {
    this._componentStore.saveTransactionType$({ editData: this.editData });
  }

  downloadTemplate() {
    const transactionTypeNameValue = this.addTransactionTypeForm?.form?.get(
      'transactionTypeDescription',
    )?.value;

    const isUpcIdentified =
      this.addTransactionTypeForm?.form?.get('isUpcIdentified')?.value;

    const isEsnBaseTemplate = isUpcIdentified === false;

    this._componentStore.downloadTemplate(
      transactionTypeNameValue,
      isEsnBaseTemplate,
    );
  }

  hasRequiredError(controlName: string): boolean {
    const c = this.addTransactionTypeForm?.form?.get(controlName);
    return !!(c && c.hasError('required') && (c.touched || c.dirty));
  }

  private markFileUploadChanged() {
    const ctrl = this.addTransactionTypeForm.form.get('fileUpload');
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
    ctrl?.updateValueAndValidity();
    this.addTransactionTypeForm.form.markAsDirty();
  }

  private markSupportingDocumentFileChanged() {
    const ctrl = this.addTransactionTypeForm.form.get('supportingDocumentFile');
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
    ctrl?.updateValueAndValidity();
    this.addTransactionTypeForm.form.markAsDirty();
  }

  private enterEditMode(data: any) {
    if (!data) return;

    const code = data?.code;
    if (code) {
      this._componentStore.loadTemplate(code, (file) => {
        this.hydrateUploadWithFile(file);
      });
    }
    if (code && data?.s3KeySupportingDocument) {
      this._componentStore.loadSupportingDocument(code, (file) => {
        this.hydrateUploadWithSupportingDocumentFile(file);
      });
    }

    this.addTransactionTypeForm.form.patchValue({
      transactionTypeDescription:
        data.transactionTypeDescription ?? data.name ?? '',
      invoiceHeaderDescription: data.invoiceHeaderDescription ?? '',
      invoicePrefix: data.invoicePrefix ?? data.prefix ?? data.code ?? '',
      costCenter: data.costCenter ?? '',
      chain: data.chain ?? '',
      spendCategory: data.spendCategoryId ?? data.spendCategory ?? null,
      invoiceType: data.isGroupedByEsn === true,
      fileUpload: null,
      hasEpFee: data.hasEpFee === true,
      hasSupportingDocument: data.hasSupportingDocument === true,
      supportingDocumentFile: null,
      isHandKey: data.isHandKey === true,
      isUpcIdentified: data.lineIdentifier === 'Upc',
    });

    this.addTransactionTypeForm.form
      .get('invoicePrefix')
      ?.disable({ emitEvent: false });

    this.addTransactionTypeForm.form
      .get('isHandKey')
      ?.disable({ emitEvent: false });

    this.addTransactionTypeForm.toggleNonHandKeyControls(data.isHandKey);
    this.addTransactionTypeForm.form.markAsPristine();
    this.addTransactionTypeForm.form.updateValueAndValidity({
      emitEvent: false,
    });
    this.originalEditSnapshot = this.addTransactionTypeForm.form.getRawValue();
  }

  private hydrateUploadWithFile(file: File) {
    const upload: IFileUpload = {
      uid: `existing-${Date.now()}`,
      name: file.name,
      size: file.size,
      progress: 100,
      uploading: false,
      uploadComplete: true,
      validationErrors: [],
      rawFile: file as Blob,
    } as any;

    this.fileUploads = [upload];
    this.addTransactionTypeForm.form
      .get('fileUpload')
      ?.setValue(this.fileUploads);

    this.isSelectedFileValid = true;

    this.addTransactionTypeForm.form.markAsPristine();
    this.addTransactionTypeForm.form.updateValueAndValidity({
      emitEvent: false,
    });
  }

  private hydrateUploadWithSupportingDocumentFile(file: File) {
    const upload: IFileUpload = {
      uid: `existing-${Date.now()}`,
      name: file.name,
      size: file.size,
      progress: 100,
      uploading: false,
      uploadComplete: true,
      validationErrors: [],
      rawFile: file as Blob,
    } as IFileUpload;

    this.supportingDocumentFileUploads = [upload];
    this.addTransactionTypeForm.form
      .get('supportingDocumentFile')
      ?.setValue(this.supportingDocumentFileUploads);

    this.isSelectedSupportingDocumentFileValid = true;

    this.addTransactionTypeForm.form.markAsPristine();
    this.addTransactionTypeForm.form.updateValueAndValidity({
      emitEvent: false,
    });
  }

  onHasSupportingDocumentChange(event: Event) {
    const value = this.addTransactionTypeForm.form.get(
      'hasSupportingDocument',
    )?.value;
    const supportingDocumentFileControl = this.addTransactionTypeForm.form.get(
      'supportingDocumentFile',
    );
    if (value) {
      const files = supportingDocumentFileControl?.value;
      if (!files || files.length === 0) {
        supportingDocumentFileControl?.setErrors({ required: true });
      } else {
        const file = files[0];
        if (file?.name && file.name.toLowerCase().endsWith('.pdf')) {
          supportingDocumentFileControl?.setErrors(null);
        } else {
          supportingDocumentFileControl?.setErrors({ invalidFileType: true });
        }
      }
    } else {
      supportingDocumentFileControl?.setValue([]);
      supportingDocumentFileControl?.setErrors(null);
    }
  }

  get isHandKey(): boolean {
    if (this.isHandKeyFFEnabled === false) {
      return false;
    }
    return this.addTransactionTypeForm.form.get('isHandKey')?.value === true;
  }

  onHandKeyChange() {
    if (this.isHandKey) {
      this.fileUploads = [];
      this.supportingDocumentFileUploads = [];
    }
    this.addTransactionTypeForm.toggleNonHandKeyControls(this.isHandKey);
  }

  handleCostCenterFilter(input: string): void {
    this._componentStore.loadCostCenters(input);
    this.loadingCostCenters = true;
  }
}
